import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { MasteryCriteriaChangeTypeEnum } from '../../../../constants/general';
import { goalActions } from '../../../../../../../../../store';
import RatesMCRow from '../common/components/ratesMCRow';

import type {
  ISAGRateMasteryCriteriaItem,
  ITreatmentPlanGoal,
} from '../../../../../../../../../utils/types';
import type { ISAGRatesMastryCriteriaState } from '../common/types/states';

interface IPercentageMasteryCriteria {
  goal: ITreatmentPlanGoal;
  goalIndex: number;
  isEditable: boolean;
}

function isSAGRateMasteryCriteria(
  criteria: ITreatmentPlanGoal['mastery_criteria'],
): criteria is ISAGRateMasteryCriteriaItem {
  if (!criteria || !('acquisition' in criteria) || !('maintenance_frequency' in criteria)) {
    return false;
  }
  const { acquisition } = criteria;
  if (!acquisition || !('change_type' in acquisition)) {
    return false;
  }
  return !('measurement_mode' in acquisition) && !('practice_duration_unit' in acquisition);
}

function SAGRateMasteryCriteriaV2({ goal, goalIndex, isEditable }: IPercentageMasteryCriteria) {
  const dispatch = useDispatch();

  const { mastery_criteria, measurement_units } = goal;
  const rateMasteryCriteria = isSAGRateMasteryCriteria(mastery_criteria)
    ? mastery_criteria
    : null;
  const rateMeasurementUnits =
    measurement_units && 'interval_size' in measurement_units ? measurement_units : null;
  const { acquisition, generalization, maintenance, maintenance_frequency } =
    rateMasteryCriteria ?? {};

  const [isAcquisitionLoaded, setIsAcquisitionLoaded] = useState(Boolean(acquisition));

  const [masteryCriteriaState, setMasteryCriteriaMCState] = useState<ISAGRatesMastryCriteriaState>({
    acquisition: {
      change_type: acquisition?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
      desired_amount: acquisition?.desired_amount ?? null,
      practice_duration_count: acquisition?.practice_duration_count ?? null,
      total_attempts: acquisition?.total_attempts ?? null,
    },
    generalization: {
      change_type: generalization?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
      desired_amount: generalization?.desired_amount ?? null,
      practice_duration_count: generalization?.practice_duration_count ?? null,
      total_attempts: generalization?.total_attempts ?? null,
    },
    maintenance: {
      change_type: maintenance?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
      desired_amount: maintenance?.desired_amount ?? null,
      practice_duration_count: maintenance?.practice_duration_count ?? null,
      total_attempts: maintenance?.total_attempts ?? null,
    },
    maintenance_frequency: maintenance_frequency ?? null,
  });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (acquisition && !isAcquisitionLoaded) {
      setIsAcquisitionLoaded(true);
    }
  }, [acquisition]);

  useEffect(() => {
    const { mastery_criteria } = goal;
    const rateMasteryCriteria = isSAGRateMasteryCriteria(mastery_criteria)
      ? mastery_criteria
      : null;
    const { acquisition, generalization, maintenance, maintenance_frequency } =
      rateMasteryCriteria ?? {};
    if (!goal?.is_editing) {
      setIsDirty(false);
    }
    setMasteryCriteriaMCState({
      acquisition: {
        change_type: acquisition?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: acquisition?.desired_amount ?? null,
        practice_duration_count: acquisition?.practice_duration_count ?? null,
        total_attempts: acquisition?.total_attempts ?? null,
      },
      generalization: {
        change_type: generalization?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: generalization?.desired_amount ?? null,
        practice_duration_count: generalization?.practice_duration_count ?? null,
        total_attempts: generalization?.total_attempts ?? null,
      },
      maintenance: {
        change_type: maintenance?.change_type ?? MasteryCriteriaChangeTypeEnum.INCREASE,
        desired_amount: maintenance?.desired_amount ?? null,
        practice_duration_count: maintenance?.practice_duration_count ?? null,
        total_attempts: maintenance?.total_attempts ?? null,
      },
      maintenance_frequency: maintenance_frequency ?? null,
    });
  }, [goal?.is_editing, isAcquisitionLoaded, goal?.id]);

  const handleAcquisitionMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      acquisition: {
        ...prev.acquisition,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  const handleGeneralizationMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      generalization: {
        ...prev.generalization,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  const handleMaintenanceMCState = (key: string, value: number | string | null) => {
    setMasteryCriteriaMCState((prev) => ({
      ...prev,
      maintenance: {
        ...prev.maintenance,
        [key]: value,
      },
    }));
    setIsDirty(true);
  };

  useEffect(() => {
    if (!isDirty) return;
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'mastery_criteria',
        value: masteryCriteriaState,
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  }, [masteryCriteriaState]);

  return (
    <>
      <RatesMCRow
        onUpdate={handleAcquisitionMCState}
        goalIndex={goalIndex}
        title="Acquisition"
        dataTestId="acquisitionMcRow"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={goal.baseline_at_creation === null ? null : Number(goal.baseline_at_creation)}
        values={masteryCriteriaState.acquisition}
        isEditable={isEditable}
        measurementUnits={rateMeasurementUnits}
      />
      <RatesMCRow
        onUpdate={handleGeneralizationMCState}
        goalIndex={goalIndex}
        title="Generalization"
        dataTestId="generalizationMcRow"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={goal.baseline_at_creation === null ? null : Number(goal.baseline_at_creation)}
        values={masteryCriteriaState.generalization}
        isEditable={isEditable}
        measurementUnits={rateMeasurementUnits}
      />
      <RatesMCRow
        onUpdate={handleMaintenanceMCState}
        goalIndex={goalIndex}
        title="Maintenance"
        dataTestId="maintenanceMcRow"
        isEditing={Boolean(goal?.is_editing)}
        baseline_at_creation={goal.baseline_at_creation === null ? null : Number(goal.baseline_at_creation)}
        values={masteryCriteriaState.maintenance}
        maintenance_frequency={masteryCriteriaState.maintenance_frequency}
        isEditable={isEditable}
        showFrequency
        updateMaintenanceFrequency={(val) => {
          setMasteryCriteriaMCState((prev) => ({
            ...prev,
            maintenance_frequency: val,
          }));
          setIsDirty(true);
        }}
        measurementUnits={rateMeasurementUnits}
      />
    </>
  );
}

export default SAGRateMasteryCriteriaV2;
